import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Globe, Plus, Trash, PencilSimple, X, MagnifyingGlass,
  Copy, Warning, Link as LinkIcon, Key, Database, Terminal,
  Pulse, ArrowClockwise, CheckCircle, XCircle, ArrowSquareOut,
  UploadSimple, DownloadSimple, EnvelopeSimple,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";

const CRED_TYPES = [
  { id: "ssh", label: "SSH", icon: Terminal, port: 22 },
  { id: "sftp", label: "SFTP", icon: Terminal, port: 22 },
  { id: "ftp", label: "FTP", icon: Terminal, port: 21 },
  { id: "mysql", label: "MySQL", icon: Database, port: 3306 },
  { id: "postgres", label: "PostgreSQL", icon: Database, port: 5432 },
  { id: "phpmyadmin", label: "phpMyAdmin", icon: Database, port: null },
  { id: "cpanel", label: "cPanel", icon: Globe, port: 2083 },
  { id: "plesk", label: "Plesk", icon: Globe, port: 8443 },
  { id: "wordpress-admin", label: "WordPress Admin", icon: Globe, port: null },
  { id: "redis", label: "Redis", icon: Database, port: 6379 },
  { id: "webmail", label: "Webmail", icon: Globe, port: null },
  { id: "other", label: "Autre", icon: Key, port: null },
];

const expiryBadge = (days) => {
  if (days == null) return null;
  if (days < 0) return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">Expiré {Math.abs(days)}j</span>;
  if (days < 30) return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{days}j</span>;
  if (days < 90) return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800">{days}j</span>;
  return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{days}j</span>;
};

export default function WebHosting() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("accesses");
  return (
    <div className="p-6 max-w-6xl">
      <button onClick={() => nav("/modules")} data-testid="wh-back" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16}/> Modules
      </button>
      <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2"><Globe size={26}/> Hébergement web</h1>
      <p className="text-sm text-muted-foreground mb-6">Accès serveurs, domaines, WHOIS live et diagnostic DNS façon intodns.com.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="accesses" data-testid="wh-tab-accesses"><Key size={14} className="mr-1.5"/> Hébergement</TabsTrigger>
          <TabsTrigger value="domains" data-testid="wh-tab-domains"><Globe size={14} className="mr-1.5"/> Domaines</TabsTrigger>
          <TabsTrigger value="uptime" data-testid="wh-tab-uptime"><Pulse size={14} className="mr-1.5"/> Uptime</TabsTrigger>
          <TabsTrigger value="typosquat" data-testid="wh-tab-typosquat"><Warning size={14} className="mr-1.5"/> Typosquat</TabsTrigger>
          <TabsTrigger value="whois" data-testid="wh-tab-whois"><MagnifyingGlass size={14} className="mr-1.5"/> WHOIS</TabsTrigger>
          <TabsTrigger value="dns" data-testid="wh-tab-dns"><Terminal size={14} className="mr-1.5"/> Diagnostic DNS</TabsTrigger>
        </TabsList>
        <TabsContent value="accesses"><AccessesTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="domains"><DomainsTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="uptime"><UptimeTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="typosquat"><TyposquatTab/></TabsContent>
        <TabsContent value="whois"><WhoisTab/></TabsContent>
        <TabsContent value="dns"><DnsTab/></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================= Tab 1: Hébergement (ex "Accès serveurs")
function AccessesTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [domainsList, setDomainsList] = useState([]);
  const [sending, setSending] = useState(null);
  const load = () => api.get("/web-hosting/accesses").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    api.get("/web-hosting/domains?page_size=200").then((r) => setDomainsList(r.data.items || [])).catch(() => {});
  }, []);

  const emptyEditor = {
    mode: "create", title: "", domain_id: "", company_id: "",
    client_company: "", client_email: "", client_phone: "",
    plan: "", storage_gb: "", bandwidth_gb: "", renewal_date: "",
    webmail_url: "", control_panel_url: "", description: "",
    credentials: [{ type: "ssh", host: "", port: 22, username: "", admin_url: "", vault_url: "", notes: "" }],
  };
  const openCreate = () => setEditor(emptyEditor);
  const openEdit = (it) => setEditor({
    mode: "edit", ...it,
    // Coerce nulls → "" so <input> stays controlled
    domain_id: it.domain_id || "", company_id: it.company_id || "",
    client_company: it.client_company || "", client_email: it.client_email || "",
    client_phone: it.client_phone || "", plan: it.plan || "",
    storage_gb: it.storage_gb ?? "", bandwidth_gb: it.bandwidth_gb ?? "",
    renewal_date: it.renewal_date || "", webmail_url: it.webmail_url || "",
    control_panel_url: it.control_panel_url || "", description: it.description || "",
    credentials: (it.credentials || []).map((c) => ({ ...c })),
  });

  const onCompanyChange = (cid) => {
    const co = companies.find((c) => c.id === cid);
    setEditor((prev) => ({
      ...prev,
      company_id: cid || "",
      client_company: co ? co.name : "",
      // Auto-fill only when the field is empty (never overwrite an intentional value)
      client_email: prev.client_email || (co?.reference_emails?.[0] || ""),
      client_phone: prev.client_phone || (co?.main_phone || ""),
    }));
  };

  const save = async () => {
    if (!editor.title || editor.title.length < 2) { toast.error("Titre requis"); return; }
    try {
      const body = { ...editor };
      delete body.mode; delete body.id;
      // Coerce empties → null so backend validators are happy
      ["domain_id", "company_id", "client_company", "client_email", "client_phone",
       "plan", "renewal_date", "webmail_url", "control_panel_url"].forEach((k) => {
        if (body[k] === "") body[k] = null;
      });
      body.storage_gb = body.storage_gb === "" || body.storage_gb == null ? null : Number(body.storage_gb);
      body.bandwidth_gb = body.bandwidth_gb === "" || body.bandwidth_gb == null ? null : Number(body.bandwidth_gb);
      if (editor.mode === "create") await api.post("/web-hosting/accesses", body);
      else await api.put(`/web-hosting/accesses/${editor.id}`, body);
      toast.success("Enregistré"); setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/web-hosting/accesses/${confirmDelete.id}`); toast.success("Supprimé"); setConfirmDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const sendCreds = async (it) => {
    if (!it.client_email) { toast.error("Renseignez d'abord l'email du client sur cette fiche"); return; }
    if (!window.confirm(`Envoyer les informations d'hébergement à ${it.client_email} ?`)) return;
    setSending(it.id);
    try {
      const r = await api.post(`/web-hosting/accesses/${it.id}/send-credentials`);
      toast.success(`Envoyé à ${r.data.sent_to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi échoué"); }
    finally { setSending(null); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copié"); };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={openCreate} data-testid="wh-access-new"><Plus size={16} className="mr-1"/> Nouvelle fiche</Button>
      </div>
      {items.length === 0 && <div className="rounded-xl border-2 border-dashed p-8 text-center text-muted-foreground">Aucune fiche d'hébergement.</div>}
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border bg-card p-4" data-testid={`wh-access-${it.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate flex items-center gap-2">
                  {it.title}
                  {it.domain && <span className="text-[0.65rem] mono px-1.5 py-0.5 rounded bg-primary/10 text-primary">{it.domain}</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {it.client_company || "—"}
                  {it.plan && <> · {it.plan}</>}
                  {it.client_phone && <> · ☎ {it.client_phone}</>}
                  {it.client_email && <> · ✉ {it.client_email}</>}
                </div>
                {it.renewal_date && (
                  <div className="text-xs mt-1 flex items-center gap-2">
                    Renouvellement : <span className="mono">{it.renewal_date}</span>
                    {(() => {
                      const days = Math.ceil((new Date(it.renewal_date) - new Date()) / 86400000);
                      return expiryBadge(days);
                    })()}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={() => sendCreds(it)} disabled={!it.client_email || sending === it.id}
                        data-testid={`wh-access-send-${it.id}`} title={it.client_email ? "Envoyer les informations au client" : "Aucun email client"}>
                  <EnvelopeSimple size={14} className="mr-1"/>{sending === it.id ? "Envoi…" : "Envoyer"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(it)}><PencilSimple size={14}/></Button>
                {isAdmin && <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(it)}><Trash size={14} className="text-red-600"/></Button>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {(it.credentials || []).map((c, i) => {
                const meta = CRED_TYPES.find((t) => t.id === c.type) || CRED_TYPES[CRED_TYPES.length - 1];
                const Icon = meta.icon;
                return (
                  <div key={i} className="rounded-lg border p-3 bg-muted/30 text-sm space-y-1">
                    <div className="flex items-center gap-2 font-medium"><Icon size={14}/> {meta.label}</div>
                    {c.host && (
                      <div className="text-xs"><span className="text-muted-foreground">Hôte : </span><span className="mono">{c.host}{c.port ? `:${c.port}` : ""}</span>
                        <button onClick={() => copy(`${c.host}${c.port ? `:${c.port}` : ""}`)} className="ml-1 text-primary"><Copy size={11}/></button></div>
                    )}
                    {c.username && (
                      <div className="text-xs"><span className="text-muted-foreground">Utilisateur : </span><span className="mono">{c.username}</span>
                        <button onClick={() => copy(c.username)} className="ml-1 text-primary"><Copy size={11}/></button></div>
                    )}
                    {c.admin_url && <a href={c.admin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1"><LinkIcon size={11}/> Ouvrir l'admin</a>}
                    {c.vault_url && <a href={c.vault_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 ml-2"><Key size={11}/> Coffre</a>}
                    {c.notes && <div className="text-xs text-muted-foreground italic mt-1">{c.notes}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editor?.mode === "create" ? "Nouvelle fiche hébergement" : "Modifier la fiche"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Titre</Label><Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} placeholder="Ex. Hébergement Acme prod" data-testid="wh-access-title"/></div>
                <div><Label>Formule</Label><Input value={editor.plan} onChange={(e) => setEditor({ ...editor, plan: e.target.value })} placeholder="Mutualisé Perso Small…" data-testid="wh-access-plan"/></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom de domaine</Label>
                  <select data-testid="wh-access-domain" value={editor.domain_id || ""}
                          onChange={(e) => setEditor({ ...editor, domain_id: e.target.value || "" })}
                          className="w-full h-9 rounded-md border bg-background px-3 text-sm">
                    <option value="">— Aucun (saisir dans Domaines) —</option>
                    {domainsList.map((d) => <option key={d.id} value={d.id}>{d.domain}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Client</Label>
                  <select data-testid="wh-access-company" value={editor.company_id || ""}
                          onChange={(e) => onCompanyChange(e.target.value)}
                          className="w-full h-9 rounded-md border bg-background px-3 text-sm">
                    <option value="">— Aucune société —</option>
                    {companies.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Email client</Label><Input type="email" value={editor.client_email} onChange={(e) => setEditor({ ...editor, client_email: e.target.value })} placeholder="contact@client.fr" data-testid="wh-access-email"/></div>
                <div><Label>Téléphone client</Label><Input value={editor.client_phone} onChange={(e) => setEditor({ ...editor, client_phone: e.target.value })} placeholder="04 74 12 34 56" data-testid="wh-access-phone"/></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label>Disque (Go)</Label><Input type="number" min={0} step="any" value={editor.storage_gb} onChange={(e) => setEditor({ ...editor, storage_gb: e.target.value })} data-testid="wh-access-storage"/></div>
                <div><Label>Bande passante (Go)</Label><Input type="number" min={0} step="any" value={editor.bandwidth_gb} onChange={(e) => setEditor({ ...editor, bandwidth_gb: e.target.value })}/></div>
                <div><Label>Date de renouvellement</Label><Input type="date" value={editor.renewal_date} onChange={(e) => setEditor({ ...editor, renewal_date: e.target.value })} data-testid="wh-access-renewal"/></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Webmail (URL)</Label><Input value={editor.webmail_url} onChange={(e) => setEditor({ ...editor, webmail_url: e.target.value })} placeholder="https://webmail.example.com"/></div>
                <div><Label>Panneau de contrôle (URL)</Label><Input value={editor.control_panel_url} onChange={(e) => setEditor({ ...editor, control_panel_url: e.target.value })} placeholder="https://cpanel.example.com"/></div>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })}/></div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Accès techniques (mots de passe stockés dans votre coffre, jamais envoyés par email)</Label>
                  <Button variant="ghost" size="sm" onClick={() => setEditor({ ...editor, credentials: [...editor.credentials, { type: "ssh", host: "", port: 22, username: "", admin_url: "", vault_url: "", notes: "" }] })}>
                    <Plus size={14} className="mr-1"/> Ajouter
                  </Button>
                </div>
                <div className="space-y-2">
                  {editor.credentials.map((c, i) => (
                    <div key={i} className="rounded-lg border p-3 space-y-2 bg-muted/20">
                      <div className="flex items-start gap-2">
                        <Select value={c.type} onValueChange={(v) => {
                          const cs = [...editor.credentials]; const meta = CRED_TYPES.find((t) => t.id === v);
                          cs[i] = { ...c, type: v, port: c.port || meta?.port || null }; setEditor({ ...editor, credentials: cs });
                        }}>
                          <SelectTrigger className="w-40"><SelectValue/></SelectTrigger>
                          <SelectContent>{CRED_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Hôte" value={c.host || ""} onChange={(e) => { const cs = [...editor.credentials]; cs[i] = { ...c, host: e.target.value }; setEditor({ ...editor, credentials: cs }); }}/>
                        <Input placeholder="Port" type="number" className="w-24" value={c.port || ""} onChange={(e) => { const cs = [...editor.credentials]; cs[i] = { ...c, port: parseInt(e.target.value) || null }; setEditor({ ...editor, credentials: cs }); }}/>
                        <Button variant="ghost" size="sm" onClick={() => setEditor({ ...editor, credentials: editor.credentials.filter((_, j) => j !== i) })}><X size={14}/></Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input placeholder="Utilisateur" value={c.username || ""} onChange={(e) => { const cs = [...editor.credentials]; cs[i] = { ...c, username: e.target.value }; setEditor({ ...editor, credentials: cs }); }}/>
                        <Input placeholder="URL admin (ex: https://phpmyadmin.exemple.fr)" value={c.admin_url || ""} onChange={(e) => { const cs = [...editor.credentials]; cs[i] = { ...c, admin_url: e.target.value }; setEditor({ ...editor, credentials: cs }); }}/>
                        <Input placeholder="Lien coffre (Bitwarden/1Password)" value={c.vault_url || ""} onChange={(e) => { const cs = [...editor.credentials]; cs[i] = { ...c, vault_url: e.target.value }; setEditor({ ...editor, credentials: cs }); }}/>
                        <Input placeholder="Notes" value={c.notes || ""} onChange={(e) => { const cs = [...editor.credentials]; cs[i] = { ...c, notes: e.target.value }; setEditor({ ...editor, credentials: cs }); }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Annuler</Button>
            <Button onClick={save} data-testid="wh-access-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Supprimer cette fiche ?" description={confirmDelete ? `« ${confirmDelete.title} » sera définitivement supprimée.` : ""}
        confirmText="Supprimer" variant="destructive" onConfirm={doDelete}/>
    </div>
  );
}

// ============================================================= Tab 2: Domains
function DomainsTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [editor, setEditor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [importReport, setImportReport] = useState(null);
  const [importing, setImporting] = useState(false);
  const [companies, setCompanies] = useState([]);

  // Load companies once (used by the editor combobox to link a domain to a real Société)
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (qDebounced) params.set("q", qDebounced);
    api.get(`/web-hosting/domains?${params}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {});
  }, [page, pageSize, qDebounced]);
  useEffect(() => { load(); }, [load]);
  // When search changes, reset to page 1
  useEffect(() => { setPage(1); }, [qDebounced, pageSize]);
  // Clear selection when list changes
  useEffect(() => { setSelected(new Set()); }, [items]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = items.length > 0 && items.every((d) => selected.has(d.id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((d) => d.id)));
  };

  const save = async () => {
    if (!editor.domain) { toast.error("Domaine requis"); return; }
    try {
      const body = { ...editor }; delete body.mode; delete body.id; delete body.days_left;
      if (editor.mode === "create") await api.post("/web-hosting/domains", body);
      else await api.put(`/web-hosting/domains/${editor.id}`, body);
      toast.success("Enregistré"); setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/web-hosting/domains/${confirmDelete.id}`); toast.success("Supprimé"); setConfirmDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const doBulkDelete = async () => {
    try {
      const r = await api.post("/web-hosting/domains/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} domaine(s) supprimé(s)`);
      setConfirmBulk(false); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const doExport = async () => {
    try {
      const r = await api.get("/web-hosting/domains/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `domains_export_${today}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${total} domaine(s) exporté(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'export"); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportReport(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/web-hosting/domains/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(r.data);
      toast.success(`Import : +${r.data.imported} nouveaux · ~${r.data.updated} mis à jour`);
      setPage(1); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import échoué"); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher domaine, registrar, client, NS…"
                 className="pl-8" data-testid="wh-domain-search"/>
        </div>
        <div className="flex-1"/>
        {isAdmin && selected.size > 0 && (
          <Button variant="destructive" onClick={() => setConfirmBulk(true)} data-testid="wh-domain-bulk-delete">
            <Trash size={14} className="mr-1.5"/> Supprimer ({selected.size})
          </Button>
        )}
        {isAdmin && (
          <Button variant="outline" data-testid="wh-domain-rematch"
                  onClick={async () => {
                    if (!window.confirm("Ré-associer chaque domaine à une société en se basant sur le nom du propriétaire (WHOIS) ?\n\nLes rattachements manuels existants sont conservés.")) return;
                    try {
                      const r = await api.post("/web-hosting/domains/rematch-companies", { force: false });
                      toast.success(`Ré-associations : ✓ ${r.data.matched} rattachés · ${r.data.skipped_manual} manuels préservés · ${r.data.unmatched} sans société`);
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                  }}>
            <ArrowClockwise size={14} className="mr-1.5"/> Ré-associer
          </Button>
        )}
        <label className="inline-flex" htmlFor="wh-domain-import-input">
          <Button variant="outline" disabled={importing} data-testid="wh-domain-import"
                  onClick={() => document.getElementById("wh-domain-import-input")?.click()}>
            <UploadSimple size={14} className="mr-1.5"/>{importing ? "Import…" : "Importer CSV"}
          </Button>
          <input id="wh-domain-import-input" data-testid="wh-domain-import-input"
                 type="file" accept=".csv,text/csv" hidden
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doImport(f); }}/>
        </label>
        <Button variant="outline" onClick={doExport} data-testid="wh-domain-export">
          <DownloadSimple size={14} className="mr-1.5"/> Exporter CSV
        </Button>
        <Button onClick={() => setEditor({ mode: "create", domain: "", registrar: "", expires_at: "", client_company: "", company_id: "", auto_renew: false, notes: "" })} data-testid="wh-domain-new">
          <Plus size={16} className="mr-1"/> Nouveau domaine
        </Button>
      </div>
      {importReport && (
        <div className="mb-3 rounded-lg border p-3 text-xs bg-muted/30" data-testid="wh-domain-import-report">
          <div className="font-semibold mb-1">Rapport d'import</div>
          <div>✓ {importReport.imported} nouveaux · ↻ {importReport.updated} mis à jour {importReport.errors.length > 0 && <span className="text-red-700">· ⚠ {importReport.errors.length} erreur(s)</span>}</div>
          {importReport.errors.slice(0, 5).map((err, i) => (
            <div key={i} className="text-red-700 mt-0.5">Ligne {err.line} — {err.domain || "?"} : {err.error}</div>
          ))}
        </div>
      )}
      {total === 0 && <div className="rounded-xl border-2 border-dashed p-8 text-center text-muted-foreground">{qDebounced ? "Aucun résultat." : "Aucun domaine."}</div>}
      {total > 0 && (
      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              {isAdmin && (
                <th className="w-9 pl-3">
                  <input type="checkbox" checked={allSelected} ref={(el) => el && (el.indeterminate = someSelected)}
                         onChange={toggleAll} data-testid="wh-domain-select-all" className="cursor-pointer"/>
                </th>
              )}
              <th className="text-left p-2.5">Domaine</th><th className="text-left">Registrar</th>
              <th className="text-left">Expiration</th><th className="text-left">Client</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className={"border-t align-top " + (selected.has(d.id) ? "bg-primary/5" : "")} data-testid={`wh-domain-${d.id}`}>
                {isAdmin && (
                  <td className="pl-3">
                    <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleOne(d.id)}
                           data-testid={`wh-domain-select-${d.id}`} className="cursor-pointer"/>
                  </td>
                )}
                <td className="p-2.5 mono">
                  {d.domain} {d.auto_renew && <span className="text-[0.6rem] px-1 rounded bg-blue-100 text-blue-800 ml-1">AUTO</span>}
                  {d.nameservers && <div className="text-[0.65rem] text-muted-foreground font-normal mt-0.5 truncate max-w-xs" title={d.nameservers}>NS : {d.nameservers}</div>}
                </td>
                <td>{d.registrar || "—"}</td>
                <td className="flex items-center gap-2 p-2.5">{d.expires_at || "—"} {expiryBadge(d.days_left)}</td>
                <td>{d.client_company || "—"}</td>
                <td className="text-right pr-2 whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditor({ mode: "edit", ...d })}><PencilSimple size={14}/></Button>
                  {isAdmin && <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(d)}><Trash size={14} className="text-red-600"/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs text-muted-foreground">
          <div>
            <span data-testid="wh-domain-total">{total}</span> domaine{total > 1 ? "s" : ""}
            {selected.size > 0 && <span className="ml-2 text-primary font-semibold">· {selected.size} sélectionné{selected.size > 1 ? "s" : ""}</span>}
          </div>
          <div className="flex items-center gap-2">
            <span>Par page :</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded border bg-background px-2 py-1" data-testid="wh-domain-page-size">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="wh-domain-prev">‹ Préc.</Button>
            <span data-testid="wh-domain-page-info">Page {page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="wh-domain-next">Suiv. ›</Button>
          </div>
        </div>
      )}

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editor?.mode === "create" ? "Nouveau domaine" : "Modifier"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div><Label>Domaine</Label><Input value={editor.domain} onChange={(e) => setEditor({ ...editor, domain: e.target.value.trim().toLowerCase() })} placeholder="exemple.fr" data-testid="wh-domain-name"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Registrar</Label><Input value={editor.registrar || ""} onChange={(e) => setEditor({ ...editor, registrar: e.target.value })} placeholder="OVH, Gandi, Cloudflare…"/></div>
                <div><Label>Date d'expiration</Label><Input type="date" value={editor.expires_at || ""} onChange={(e) => setEditor({ ...editor, expires_at: e.target.value })}/></div>
              </div>
              <div><Label>Client</Label>
                <select
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={editor.company_id || ""}
                  onChange={(e) => {
                    const cid = e.target.value || "";
                    const co = companies.find((c) => c.id === cid);
                    setEditor({ ...editor, company_id: cid, client_company: co ? co.name : "" });
                  }}
                  data-testid="wh-domain-company">
                  <option value="">— Aucune société —</option>
                  {companies.map((co) => (
                    <option key={co.id} value={co.id}>{co.name}{co.email_domains && co.email_domains.length ? ` (${co.email_domains.join(", ")})` : ""}</option>
                  ))}
                </select>
                {editor.company_id === "" && editor.client_company && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="wh-domain-company-orphan">
                    ⓘ Client libre « {editor.client_company} » — non rattaché à une société existante.
                  </p>
                )}
              </div>
              {editor.nameservers && <div><Label className="text-xs">Nameservers (importés)</Label><Input value={editor.nameservers} readOnly className="mono text-xs bg-muted/40"/></div>}
              {(editor.owner_name || editor.owner_email || editor.owner_phone) && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-900 p-3 space-y-1" data-testid="wh-domain-owner-panel">
                  <div className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-1">Propriétaire (extrait du WHOIS)</div>
                  {editor.owner_name  && <div className="text-xs"><span className="text-muted-foreground">Nom : </span><span className="mono">{editor.owner_name}</span></div>}
                  {editor.owner_email && <div className="text-xs"><span className="text-muted-foreground">Email : </span><span className="mono">{editor.owner_email}</span></div>}
                  {editor.owner_phone && <div className="text-xs"><span className="text-muted-foreground">Tél : </span><span className="mono">{editor.owner_phone}</span></div>}
                  <p className="text-[0.65rem] text-blue-900/70 dark:text-blue-200/70 mt-1">Ces coordonnées sont utilisées en priorité pour les rappels de renouvellement si aucune société n'est rattachée. Le rattachement automatique se fait uniquement sur le <b>nom</b> — jamais sur l'adresse email — pour éviter les faux positifs (ex. hébergement revendu).</p>
                </div>
              )}
              {editor.owner_contact && <div><Label className="text-xs">Contact propriétaire (brut)</Label><Textarea value={editor.owner_contact} readOnly rows={2} className="text-xs bg-muted/40"/></div>}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editor.auto_renew} onChange={(e) => setEditor({ ...editor, auto_renew: e.target.checked })}/> Renouvellement automatique activé</label>
              <div><Label>Notes</Label><Textarea rows={2} value={editor.notes || ""} onChange={(e) => setEditor({ ...editor, notes: e.target.value })}/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Annuler</Button>
            <Button onClick={save} data-testid="wh-domain-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Supprimer ce domaine ?" description={confirmDelete ? `${confirmDelete.domain} sera retiré du registre.` : ""}
        confirmText="Supprimer" variant="destructive" onConfirm={doDelete}/>
      <ConfirmDialog open={confirmBulk} onOpenChange={setConfirmBulk}
        title={`Supprimer ${selected.size} domaine${selected.size > 1 ? "s" : ""} ?`}
        description="Cette action est définitive. Les enregistrements sélectionnés seront retirés du registre."
        confirmText="Tout supprimer" variant="destructive" onConfirm={doBulkDelete}/>
    </div>
  );
}

// ============================================================= Tab 3: WHOIS
function WhoisTab() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!domain.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.get(`/web-hosting/whois?domain=${encodeURIComponent(domain.trim().toLowerCase())}`);
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input placeholder="exemple.fr" value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid="wh-whois-input"/>
        <Button onClick={run} disabled={busy} data-testid="wh-whois-run">{busy ? "…" : "Interroger"}</Button>
      </div>
      {result && (
        <div className="rounded-xl border bg-card p-4 space-y-2" data-testid="wh-whois-result">
          {result.available ? (
            <div className="text-emerald-700 font-semibold">✓ Domaine {result.domain} disponible à l'achat (RDAP 404)</div>
          ) : (
            <>
              <div className="text-lg font-semibold">{result.domain}</div>
              <table className="w-full text-sm">
                <tbody>
                  <tr><td className="py-1 text-muted-foreground w-40">Registrar</td><td className="font-medium">{result.registrar || "—"}</td></tr>
                  <tr><td className="py-1 text-muted-foreground">Date de création</td><td className="mono">{result.created_at || "—"}</td></tr>
                  <tr><td className="py-1 text-muted-foreground">Dernière MAJ</td><td className="mono">{result.updated_at || "—"}</td></tr>
                  <tr><td className="py-1 text-muted-foreground">Expiration</td><td className="mono font-semibold">{result.expires_at || "—"}</td></tr>
                  <tr><td className="py-1 text-muted-foreground">Statut</td><td className="text-xs">{(result.status || []).join(", ") || "—"}</td></tr>
                  <tr><td className="py-1 text-muted-foreground">Nameservers</td><td className="mono text-xs">{(result.nameservers || []).join(", ") || "—"}</td></tr>
                  <tr><td className="py-1 text-muted-foreground">Handle</td><td className="mono text-xs">{result.handle || "—"}</td></tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================= Tab 5: Uptime Monitor
function UptimeTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [checking, setChecking] = useState(null);
  const load = () => api.get("/web-hosting/uptime/sites").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, []);

  const save = async () => {
    if (!editor.url || !/^https?:\/\//.test(editor.url)) { toast.error("URL http(s):// requise"); return; }
    try {
      const body = { ...editor }; delete body.mode; delete body.id; delete body.recent; delete body.uptime_24h;
      if (editor.mode === "create") await api.post("/web-hosting/uptime/sites", body);
      else await api.put(`/web-hosting/uptime/sites/${editor.id}`, body);
      toast.success("Enregistré"); setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const doDelete = async () => {
    try { await api.delete(`/web-hosting/uptime/sites/${confirmDelete.id}`); toast.success("Supprimé"); setConfirmDelete(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const checkNow = async (id) => {
    setChecking(id);
    try {
      const r = await api.post(`/web-hosting/uptime/check-now/${id}`);
      toast.success(r.data.ok ? `✓ UP (${r.data.status}, ${r.data.ms}ms)` : `✗ DOWN (${r.data.status || r.data.error || "erreur"})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setChecking(null); }
  };

  const statusBadge = (s) => {
    if (s === "up") return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">UP</span>;
    if (s === "down") return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">DOWN</span>;
    return <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">?</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">Vérification HTTP toutes les 15 min · push si transition UP → DOWN</p>
        <Button onClick={() => setEditor({ mode: "create", url: "", label: "", client_company: "", enabled: true, alert_on_down: true })} data-testid="wh-uptime-new">
          <Plus size={16} className="mr-1"/> Nouveau site
        </Button>
      </div>
      {items.length === 0 && <div className="rounded-xl border-2 border-dashed p-8 text-center text-muted-foreground">Aucun site monitoré.</div>}
      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="rounded-xl border bg-card p-4" data-testid={`wh-uptime-${s.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold truncate">{s.label || s.url}</span>
                  {statusBadge(s.current_status)}
                  {!s.enabled && <span className="text-[0.65rem] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Désactivé</span>}
                </div>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mono truncate">
                  {s.url} <ArrowSquareOut size={11}/>
                </a>
                {s.client_company && <div className="text-xs text-muted-foreground">{s.client_company}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => checkNow(s.id)} disabled={checking === s.id} data-testid={`wh-uptime-check-${s.id}`}>
                  <ArrowClockwise size={14} className={checking === s.id ? "animate-spin" : ""}/>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditor({ mode: "edit", ...s })}><PencilSimple size={14}/></Button>
                {isAdmin && <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(s)}><Trash size={14} className="text-red-600"/></Button>}
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {s.uptime_24h != null && (
                <span className={s.uptime_24h >= 99 ? "text-emerald-700 font-semibold" : s.uptime_24h >= 95 ? "text-amber-700 font-semibold" : "text-red-700 font-semibold"}>
                  Uptime 24h : {s.uptime_24h}%
                </span>
              )}
              {s.recent && s.recent.length > 0 && (
                <div className="flex gap-0.5 items-end" title="24 dernières vérifications (récentes à gauche)">
                  {s.recent.map((c, i) => (
                    <div key={i} title={`${new Date(c.at).toLocaleString("fr-FR")} · ${c.ok ? "UP" : "DOWN"} · ${c.status} · ${c.ms}ms`}
                         className={"w-1.5 h-4 rounded-sm " + (c.ok ? "bg-emerald-500" : "bg-red-500")}/>
                  ))}
                </div>
              )}
              {s.last_ok && <span className="text-muted-foreground">Dernier UP : {new Date(s.last_ok).toLocaleString("fr-FR")}</span>}
              {s.last_down && s.current_status === "down" && <span className="text-red-700">Down depuis : {new Date(s.last_down).toLocaleString("fr-FR")}</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editor?.mode === "create" ? "Nouveau site à monitorer" : "Modifier le site"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div><Label>URL</Label><Input value={editor.url} onChange={(e) => setEditor({ ...editor, url: e.target.value })} placeholder="https://exemple.fr" data-testid="wh-uptime-url"/></div>
              <div><Label>Libellé (optionnel)</Label><Input value={editor.label || ""} onChange={(e) => setEditor({ ...editor, label: e.target.value })} placeholder="Site vitrine Acme"/></div>
              <div><Label>Client</Label><Input value={editor.client_company || ""} onChange={(e) => setEditor({ ...editor, client_company: e.target.value })}/></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editor.enabled} onChange={(e) => setEditor({ ...editor, enabled: e.target.checked })}/> Monitoring activé</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editor.alert_on_down} onChange={(e) => setEditor({ ...editor, alert_on_down: e.target.checked })}/> Alerte push quand le site tombe</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Annuler</Button>
            <Button onClick={save} data-testid="wh-uptime-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Supprimer ce site ?" description={confirmDelete ? `${confirmDelete.label || confirmDelete.url} et son historique de checks seront supprimés.` : ""}
        confirmText="Supprimer" variant="destructive" onConfirm={doDelete}/>
    </div>
  );
}

// ============================================================= Tab 6: Typosquat
function TyposquatTab() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!domain.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.get(`/web-hosting/typosquat?domain=${encodeURIComponent(domain.trim().toLowerCase())}`);
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-3 text-xs text-amber-900 dark:text-amber-200">
        <b>Comment ça marche :</b> on génère des variations classiques (permutations, lettres manquantes/dupliquées, homoglyphes, TLD alternatifs) et on vérifie via RDAP si chaque variation est enregistrée. Un domaine <b>déposé</b> par un tiers est un signal de typosquatting probable.
      </div>
      <div className="flex gap-2">
        <Input placeholder="votresociete.fr" value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid="wh-typo-input"/>
        <Button onClick={run} disabled={busy} data-testid="wh-typo-run">{busy ? "…" : "Détecter"}</Button>
      </div>
      {result && (
        <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="wh-typo-result">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-semibold">Base :</span>
            <span className="mono">{result.base}</span>
            <span className={"text-xs px-2 py-1 rounded-full font-semibold " + (result.suspicious_count > 0 ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800")}>
              {result.suspicious_count} variation(s) enregistrée(s) sur {result.variations.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {result.variations.map((v) => (
              <div key={v.domain} data-testid={`wh-typo-var-${v.domain}`}
                   className={"flex items-center justify-between gap-2 rounded-lg border p-2.5 text-sm " + (v.registered ? "border-red-300 bg-red-50 dark:bg-red-950/20" : "bg-muted/20")}>
                <span className="mono truncate">{v.domain}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {v.registered === true ? (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-600 text-white font-semibold inline-flex items-center gap-1"><XCircle size={11} weight="fill"/> DÉPOSÉ</span>
                  ) : v.registered === false ? (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><CheckCircle size={11} weight="fill"/> Libre</span>
                  ) : (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">?</span>
                  )}
                  <a href={`https://${v.domain}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/70" title="Ouvrir"><ArrowSquareOut size={13}/></a>
                </div>
              </div>
            ))}
          </div>
          {result.suspicious_count > 0 && (
            <div className="text-xs text-red-800 bg-red-50 dark:bg-red-950/20 dark:text-red-200 border border-red-200 dark:border-red-900 rounded-lg p-2.5">
              <b>⚠ Action recommandée :</b> vérifiez si ces domaines pointent vers du contenu concurrent/malveillant, envisagez un dépôt défensif ou une opposition juridique.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================= Tab 4: DNS diag
function DnsTab() {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!domain.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.get(`/web-hosting/dns-check?domain=${encodeURIComponent(domain.trim().toLowerCase())}`);
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Input placeholder="exemple.fr" value={domain} onChange={(e) => setDomain(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid="wh-dns-input"/>
        <Button onClick={run} disabled={busy} data-testid="wh-dns-run">{busy ? "…" : "Diagnostiquer"}</Button>
      </div>
      {result && (
        <div className="space-y-3" data-testid="wh-dns-result">
          <div className="rounded-xl border bg-card p-4">
            <h3 className="font-semibold mb-3">Contrôles</h3>
            {result.checks.map((c, i) => (
              <div key={i} className="flex items-start gap-3 py-1.5 border-b last:border-0">
                <span className={c.ok ? "text-emerald-600" : "text-red-600"}>{c.ok ? "✅" : "❌"}</span>
                <div className="flex-1 text-sm">
                  <div className="font-medium">{c.label}</div>
                  <div className="text-xs text-muted-foreground mono truncate">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border bg-card p-4">
            <h3 className="font-semibold mb-3">Propagation (3 resolvers publics)</h3>
            {result.propagation.map((p, i) => (
              <div key={i} className="flex items-center gap-3 py-1 text-sm">
                <span className="w-24 text-muted-foreground">{p.resolver}</span>
                <span className="mono text-xs w-24">{p.ip}</span>
                <span className={p.ok ? "text-emerald-700" : "text-red-600"}>{p.answers.join(", ") || "∅"}</span>
              </div>
            ))}
            {result.reverse_ptr && <div className="mt-3 text-sm text-muted-foreground">Reverse PTR : <span className="mono">{result.reverse_ptr}</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}
